// src/services/api.ts
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import secureLocalStorage from 'react-secure-storage';
import { tokenService } from './tokenService';
import { STORAGE_KEYS } from '../constants';
import { expireSession } from './sessionService';

const BASE_URL = process.env.REACT_APP_API_URL;

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── Request interceptor ─────────────────────────────────────────────────────
// Proactively refresh the token when it is close to expiry, then attach it.
// Uses tokenService's internal promise-deduplication so concurrent requests
// never trigger more than one refresh at a time.
api.interceptors.request.use(
  async (config) => {
    if (tokenService.shouldRefreshToken()) {
      try {
        await tokenService.refreshToken();
      } catch {
        // Continue — the response interceptor will handle a real 401
      }
    }

    const token = secureLocalStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response interceptor ────────────────────────────────────────────────────
// On a real 401 (expired token that wasn't caught proactively), attempt one
// refresh and replay the original request. A _retry flag prevents infinite loops.
interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const expireCurrentSession = () => {
  tokenService.stopTokenRefreshMonitor();
  expireSession();
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refreshtoken') &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      originalRequest._retry = true;

      try {
        // tokenService deduplicates concurrent refresh calls
        await tokenService.refreshToken();

        const newToken = secureLocalStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      } catch {
        expireCurrentSession();
        return Promise.reject(error);
      }
    }

    // Any other 401 (e.g. on the refresh endpoint itself) → logout
    // Exclude /auth/login so login errors are handled by the login page itself
    if (error.response?.status === 401 && !originalRequest?.url?.includes('/auth/login')) {
      expireCurrentSession();
    }

    return Promise.reject(error);
  }
);
